import { useEffect, useRef, useState } from 'react';
import { signOut, type Me } from '../identity.js';

/**
 * The account, top right, on home and in the IDE: who you are, settings, access
 * management for admins (prd-deltas §22), and signing out. Each item reaches the same
 * place as its named command; this is a way to it, not the only one.
 */
export function AccountMenu({ me, onOpenSettings }: { me: Me; onOpenSettings: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="account" ref={ref}>
      <button
        className="avatar"
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={me.email}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {me.avatarUrl ? <img src={me.avatarUrl} alt="" /> : (me.email[0] ?? '?').toUpperCase()}
      </button>
      {open ? (
        <div className="account-menu" role="menu">
          <div className="account-who">
            {me.name ? <span className="account-name">{me.name}</span> : null}
            <span className="account-email">{me.email}</span>
            {me.admin ? <span className="account-role">admin</span> : null}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onOpenSettings();
            }}
          >
            Settings
          </button>
          {me.admin ? (
            <a role="menuitem" href="/admin">
              Manage access
            </a>
          ) : null}
          <button type="button" role="menuitem" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
